import { useState, useRef, useMemo, useEffect } from 'react'
import algoliasearch from 'algoliasearch'
import type { SuggestedTermHit } from './types'
import {
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { useShopify } from '@/hooks/useShopify'
import { useAlgoliaDataExtension } from '@/features/product/hooks/useAlgoliaDataExtension/useAlgoliaDataExtension'
import { ProductHit } from '@/features/product/types'
import { algoliaGlobalFilter, requestOptions } from '@/services/algolia'
import {
  createAvailableFilters,
  filtersToFacets,
  getAlgoliaStockQuery,
  isFilteringByOptions,
} from '@/services/algolia/utils'
import { type MultipleQueriesQuery } from '@algolia/client-search'
import { type NestedStringArrayRecord } from '@/services/algolia/types'

interface UseSearchOptions {
  suggestedSearchTermFallbacks?: string[]
  filters?: NestedStringArrayRecord
  isSearchFiltersEnabled?: boolean
}

const useSearch = (options: UseSearchOptions = {}) => {
  const { integrations } = useShopify()
  const { indexPrefix, appId, apiKey } = integrations.algolia
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [query, setQuery] = useState('')

  const {
    filters: filterOptions = {},
    isSearchFiltersEnabled = false,
    suggestedSearchTermFallbacks = [],
  } = options

  const searchClient = useRef(algoliasearch(appId, apiKey))

  const recommendationsIndex = useRef(
    searchClient.current.initIndex(`${indexPrefix}_products-suggestions`),
  )

  const { stop_selling_quantity = 0 } = window.Lounge.settings

  const filterStockQuery = getAlgoliaStockQuery(
    indexPrefix,
    stop_selling_quantity,
  )

  const facetFilters = isSearchFiltersEnabled
    ? filtersToFacets(filterOptions.filters)
    : undefined

  const {
    data: results,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['search', query, { ...filterOptions }],
    queryFn: async ({ pageParam = 0 }) => {
      const queries: MultipleQueriesQuery[] = [
        {
          indexName: `${indexPrefix}_products`,
          query,
          params: {
            ...requestOptions,
            page: pageParam,
            hitsPerPage: 40,
            clickAnalytics: true,
            filters:
              isSearchFiltersEnabled &&
              facetFilters &&
              isFilteringByOptions(facetFilters)
                ? `${filterStockQuery} AND ${algoliaGlobalFilter}`
                : algoliaGlobalFilter,
          },
        },
        {
          indexName: `${indexPrefix}_products`,
          query,
          params: {
            ...requestOptions,
            page: pageParam,
            facetFilters: [
              ...(facetFilters ? facetFilters : []),
              ...(filterOptions?.collections
                ? [`collections:${filterOptions?.collections}`]
                : []),
            ],
            hitsPerPage: 40,
            distinct: true,
            clickAnalytics: true,
            filters:
              isSearchFiltersEnabled &&
              facetFilters &&
              isFilteringByOptions(facetFilters)
                ? `${filterStockQuery} AND ${algoliaGlobalFilter}`
                : algoliaGlobalFilter,
          },
        },
      ]

      const {
        results: [facets, products],
      } = await searchClient.current.multipleQueries<ProductHit>(queries)

      return {
        hits: products.hits,
        page: products.page,
        nbPages: products.nbPages,
        nbHits: products.nbHits,
        queryID: products.queryID,
        facets: facets.facets,
        renderingContent: facets.renderingContent,
      }
    },
    enabled: Boolean(query),
    placeholderData: keepPreviousData,
    getNextPageParam: ({ page, nbPages }) =>
      page < nbPages - 1 ? page + 1 : undefined,
    initialPageParam: undefined as number | undefined,
  })

  const { data: suggestedSearchTerms = [] } = useQuery({
    queryKey: ['searchRecommendations'],
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: async () => {
      try {
        const { hits } =
          await recommendationsIndex.current.search<SuggestedTermHit>('', {
            hitsPerPage: 5,
          })
        return hits.map((hit) => hit.query)
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(JSON.stringify(err))

        captureException(error)
        return suggestedSearchTermFallbacks
      }
    },
    enabled: Boolean(isSearchOpen),
    refetchOnWindowFocus: false,
    placeholderData: suggestedSearchTermFallbacks,
  })

  const flattenedPages = useMemo(
    () =>
      results?.pages.flatMap(({ hits, queryID }) =>
        hits.map((hit: ProductHit) => {
          return {
            ...hit,
            queryID,
          }
        }),
      ) ?? [],
    [results?.pages],
  )

  const hits = useAlgoliaDataExtension(flattenedPages, true)

  const facets = results?.pages[0]?.facets ?? {}
  const renderingContent = results?.pages[0]?.renderingContent

  const onSearchSubmit = (query: string) => {
    const { history, location } = window
    if (query.length > 0) {
      const page = `${location.pathname}?q=${query}`
      history.pushState({ q: query }, '', page)
    } else {
      history.pushState({}, '', location.pathname)
    }
  }

  const onSearchChange = (query: string) => {
    setQuery(query)
  }

  const clearQuery = () => {
    setQuery('')
  }

  return {
    onSearchChange,
    onSearchSubmit,
    isSearchOpen,
    setIsSearchOpen,
    query,
    setQuery,
    clearQuery,
    hits,
    isLastPage: !hasNextPage,
    showMore: fetchNextPage,
    nbHits: results?.pages[0]?.nbHits || 0,
    suggestedSearchTerms,
    availableFilters: createAvailableFilters(
      facets,
      renderingContent?.facetOrdering,
    ),
  }
}

export { useSearch }
